import React, { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { formatMoney } from '../../api/bookingApi';
import { useBooking } from '../../booking/BookingContext';

function QrFallback({ value }) {
  // Lightweight visual stand-in; real QR may come from API as data URL or text.
  const isDataUrl =
    typeof value === 'string' && value.startsWith('data:image');
  if (isDataUrl) {
    return (
      <img
        src={value}
        alt="Ticket QR"
        className="w-40 h-40 mx-auto bg-white p-2 rounded"
      />
    );
  }
  return (
    <div className="w-40 h-40 mx-auto bg-white text-black text-[10px] p-2 break-all flex items-center justify-center text-center font-mono rounded">
      {value}
    </div>
  );
}

export default function TicketPage() {
  const booking = useBooking();
  const navigate = useNavigate();
  const ticket = booking.booking;
  const trip = ticket?.trip || booking.selectedTrip;

  const qrValue = useMemo(() => {
    if (ticket?.qr) return ticket.qr;
    return `SUPERFEO|${ticket?.reference || ''}|${ticket?.tripId || ''}`;
  }, [ticket]);

  if (!ticket?.reference) {
    return <Navigate to="/" replace />;
  }

  const bookAnother = () => {
    booking.reset();
    navigate('/');
  };

  const seats = (ticket.seats?.length
    ? ticket.seats
    : booking.selectedSeats
  ).join(', ');

  return (
    <div className="space-y-4">
      <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm bg-white">
        <div className="bg-black text-white px-5 py-6 text-center">
          <p className="text-xs tracking-widest text-pink-300 font-semibold">
            SUPERFEO EXPRESS
          </p>
          <h2 className="font-heading font-bold text-2xl mt-1">
            Booking confirmed
          </h2>
          <p className="mt-3 text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
            Reference number
          </p>
          <p className="mt-1 font-mono text-lg text-white break-all">
            {ticket.reference}
          </p>
        </div>
        <div className="p-5 space-y-4">
          <QrFallback value={qrValue} />
          <div className="rounded-xl border border-pink-200 bg-pink-50 px-4 py-3">
            <p className="text-xs font-semibold text-gray-500">
              Reference number
            </p>
            <p className="mt-1 font-mono text-base font-bold text-gray-900 break-all select-all">
              {ticket.reference}
            </p>
          </div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Route</dt>
              <dd className="font-semibold text-right capitalize">
                {trip?.originName || booking.origin} →{' '}
                {trip?.destinationName || booking.destination}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Date</dt>
              <dd className="font-semibold">{trip?.date || booking.date}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Departure</dt>
              <dd className="font-semibold">{trip?.departureTime || '—'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Seats</dt>
              <dd className="font-semibold">{seats || '—'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Amount</dt>
              <dd className="font-semibold text-superfeo-pink">
                {formatMoney(
                  ticket.amount || booking.total,
                  ticket.currency || trip?.currency || 'TZS'
                )}
              </dd>
            </div>
            {ticket.gate && (
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Gate</dt>
                <dd className="font-semibold">{ticket.gate}</dd>
              </div>
            )}
          </dl>
          <p className="text-xs text-gray-500 text-center pt-2">
            Show this e-ticket when boarding. Arrive early with your phone
            charged.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={bookAnother}
          className="flex-1 bg-superfeo-pink hover:bg-superfeo-darkpink text-white font-bold py-3 rounded-xl"
        >
          Book another
        </button>
      </div>
    </div>
  );
}
